import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, Copy, Download, CheckCircle2, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import AnimatedCreativePreview from "@/components/admin/AnimatedCreativePreview";
import logoSvg from "@/assets/logo.svg";

const POST_TYPES = [
  "Founder insight",
  "Investor insight",
  "Building AdV3nture",
  "Startup fundraising tip",
  "Product update",
];
const TONES = ["Thoughtful", "Bold", "Educational", "Announcement"];
const LENGTHS = ["Short", "Medium", "Long"];

interface GeneratedContent {
  linkedinPost: string;
  xPost: string;
  creativeHeadline: string;
}

export default function AdminContentGenerator() {
  const [topic, setTopic] = useState("");
  const [postType, setPostType] = useState(POST_TYPES[0]);
  const [tone, setTone] = useState(TONES[0]);
  const [postLength, setPostLength] = useState(LENGTHS[1]);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<GeneratedContent | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }
    setLoading(true);
    setContent(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-content", {
        body: { topic, postType, tone, postLength },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setContent(data);
      toast.success("Content generated successfully");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to generate content");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const downloadCreative = () => {
    if (!content) return;
    const canvas = document.createElement("canvas");
    const size = 1080;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    // Background
    ctx.fillStyle = "#0a0f1e";
    ctx.fillRect(0, 0, size, size);

    // Subtle gradient overlay
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, "rgba(20, 184, 166, 0.08)");
    grad.addColorStop(1, "rgba(20, 184, 166, 0.02)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    // Border accent
    ctx.strokeStyle = "rgba(20, 184, 166, 0.3)";
    ctx.lineWidth = 3;
    ctx.strokeRect(40, 40, size - 80, size - 80);

    // Corner accents
    ctx.strokeStyle = "#14b8a6";
    ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(40, 100); ctx.lineTo(40, 40); ctx.lineTo(100, 40); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(size - 100, 40); ctx.lineTo(size - 40, 40); ctx.lineTo(size - 40, 100); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(40, size - 100); ctx.lineTo(40, size - 40); ctx.lineTo(100, size - 40); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(size - 100, size - 40); ctx.lineTo(size - 40, size - 40); ctx.lineTo(size - 40, size - 100); ctx.stroke();

    // Main headline
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const headline = content.creativeHeadline;
    const fontSize = headline.length > 30 ? 56 : headline.length > 20 ? 64 : 72;
    ctx.font = `700 ${fontSize}px "Space Grotesk", "SF Pro Display", system-ui, sans-serif`;

    // Word wrap
    const maxWidth = size - 200;
    const words = headline.split(" ");
    const lines: string[] = [];
    let currentLine = "";
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    const lineHeight = fontSize * 1.3;
    const totalHeight = lines.length * lineHeight;
    const startY = (size / 2) - (totalHeight / 2) + lineHeight / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, size / 2, startY + i * lineHeight);
    });

    // Teal accent line
    ctx.fillStyle = "#14b8a6";
    ctx.fillRect(size / 2 - 40, startY + totalHeight + 20, 80, 4);

    // Subtext
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    ctx.font = `400 24px "Space Grotesk", system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.fillText("adv3nture.io", size / 2, size - 90);

    // Draw SVG logo
    const logoImg = new Image();
    logoImg.onload = () => {
      const logoHeight = 40;
      const logoWidth = logoImg.width * (logoHeight / logoImg.height);
      ctx.drawImage(logoImg, 70, 65, logoWidth, logoHeight);

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `adv3nture-creative-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Creative downloaded");
      }, "image/png");
    };
    logoImg.onerror = () => {
      // Fallback: download without logo
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `adv3nture-creative-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Creative downloaded");
      }, "image/png");
    };
    logoImg.src = logoSvg;
  };

  return (
    <>
      <SEOHead title="Content Generator, AdV3nture" description="Internal content generation tool" />
      <div className="min-h-screen bg-[#080d1a]">
        {/* Header */}
        <div className="border-b border-white/5 bg-[#0a0f1e]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center gap-3 mb-1">
              <Sparkles className="w-5 h-5 text-teal-400" />
              <h1 className="text-xl font-semibold text-white tracking-tight">
                AdV3nture Content Generator
              </h1>
            </div>
            <p className="text-sm text-white/40 ml-8">
              Generate LinkedIn posts, X posts, and branded social creatives in seconds.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <div className="space-y-6">
              <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-6 space-y-5">
                <div>
                  <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                    Topic
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g. Why founders should stop cold-emailing investors"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                    Post Type
                  </label>
                  <select
                    value={postType}
                    onChange={(e) => setPostType(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 transition-all appearance-none"
                  >
                    {POST_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#0c1222]">{t}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                      Tone
                    </label>
                    <select
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 transition-all appearance-none"
                    >
                      {TONES.map((t) => (
                        <option key={t} value={t} className="bg-[#0c1222]">{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                      Post Length
                    </label>
                    <select
                      value={postLength}
                      onChange={(e) => setPostLength(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/20 transition-all appearance-none"
                    >
                      {LENGTHS.map((l) => (
                        <option key={l} value={l} className="bg-[#0c1222]">{l}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading || !topic.trim()}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg text-sm transition-all flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Content
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Right: Output */}
            <div className="space-y-6">
              {!content && !loading && (
                <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
                  <Sparkles className="w-8 h-8 text-white/10 mb-4" />
                  <p className="text-sm text-white/20">
                    Generated content will appear here
                  </p>
                </div>
              )}

              {loading && (
                <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
                  <Loader2 className="w-8 h-8 text-teal-400 animate-spin mb-4" />
                  <p className="text-sm text-white/40">Generating your content...</p>
                </div>
              )}

              {content && (
                <div className="space-y-4">
                  {/* LinkedIn Post */}
                  <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">
                        LinkedIn Post
                      </h3>
                      <button
                        onClick={() => copyText(content.linkedinPost, "linkedin")}
                        className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
                      >
                        {copiedField === "linkedin" ? (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> Copy</>
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                      {content.linkedinPost}
                    </p>
                  </div>

                  {/* X Post */}
                  <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">
                        X Post
                      </h3>
                      <button
                        onClick={() => copyText(content.xPost, "x")}
                        className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
                      >
                        {copiedField === "x" ? (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> Copy</>
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                      {content.xPost}
                    </p>
                  </div>

                  {/* Creative Preview */}
                  <div className="rounded-xl border border-white/[0.06] bg-[#0c1222] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">
                        Creative Preview
                      </h3>
                      <button
                        onClick={downloadCreative}
                        className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    </div>
                    <AnimatedCreativePreview headline={content.creativeHeadline} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
